<template>
  <div class="locator-search mb-1">
    <div class="form-group mb-0">
      <div class="form-group--inline relative">
        <div class="w-full relative">
          <form @submit.prevent="search">
            <input
              v-model="textSearchQuery"
              type="text"
              name="searchQuery"
              class="form-control w-full pr-5 search-query"
              :placeholder="bp('storeFinderEnterCity')"
              :disabled="isLoading"
              autocomplete="off"
              maxlength="100"
              qaid="store-locator__search__input"
              @blur="hideAutoLocation"
              @focus="showAutoLocation"
              @keyup.enter="applyAutoCompleteOverQuery"
            />
            <trek-button
              text
              icon-only
              form-inline
              icon="search"
              type="submit"
              class="absolute top-0 right-0 mx-1"
              qaid="store-locator__search__search-button"
              @click.prevent="search"
            >
            </trek-button>
          </form>
        </div>
        <trek-button
          tertiary
          icon-only
          form-inline
          icon="tune"
          class="ml-1"
          qaid="store-locator__search__filters-button"
          :aria-label="bp('filterTitle')"
          @click.prevent="openFilters"
        >
        </trek-button>
        <ul
          v-show="(isGoogleSuggestionsVisible && !isLoading) || isAutoDetectingLocation"
          class="google-suggestions"
          qaid="store-locator__search__suggestions"
        >
          <li
            v-for="(suggestion, index) in placeSuggestions"
            :key="suggestion.id"
            :qaid="`store-locator__search__suggestions__${index}`"
            @click.prevent="selectSuggestion(suggestion)"
            @mouseover="setAutoCompleteOverQuery(suggestion)"
            @mouseleave="setAutoCompleteOverQuery()"
          >
            {{ suggestion.description }}
          </li>
          <li
            v-if="isAutoLocationSupported && !isGeolocationQueryScheduled"
            class="auto-location flex justify-between"
            qaid="store-locator__search__auto-location"
            @click.prevent="queryGeolocation"
          >
            <span>{{ bp('storeFinderUseLocation') }}</span>
            <i class="material-icons" :class="{processing: isAutoDetectingLocation}">near_me</i>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import delay from 'lodash/delay';
import get from 'lodash/get';
import {GoogleAutoComplete} from '@/components/locator/mixins/google-autocomplete';
import {ClickOutside} from '@/utils/click-outside';
import NearLocation from '@/components/locator/mixins/near-location';
import TrekButton from '@/components/TrekButton';

export default {
  name: 'LocatorSearch',
  components: {TrekButton},
  mixins: [GoogleAutoComplete, NearLocation, ClickOutside(['.google-suggestions', '.search-query'])],
  props: {
    value: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: true,
    },
    geoipLocation: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isGeolocationQueryScheduled: false,
      textSearchQuery: this.value,
      queryCenter: {},
      isGoogleSuggestionsVisible: false,
      autoCompleteOverQuery: '',
    };
  },
  watch: {
    isGoogleMapsReady(value) {
      if (value && this.isGeolocationQueryScheduled) {
        this.queryGeolocation();
      }
    },
    isAutoDetectingLocation(value) {
      if (!value) {
        this.isGeolocationQueryScheduled = false;
      }
    },
    queryCenter(val) {
      this.$emit('query-center', val);
    },
    isLoading(value) {
      if (!value) {
        this.isGoogleSuggestionsVisible = false;
      }
    },
  },
  mounted() {
    this.$on('clicked-outside', this.hideAutoLocation);
    this.$on('location-auto-detected', this.search);
  },
  methods: {
    applyAutoCompleteOverQuery(e) {
      if (this.autoCompleteOverQuery.length > 0) {
        this.textSearchQuery = this.autoCompleteOverQuery;
        this.search();
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    },

    setAutoCompleteOverQuery(query) {
      this.autoCompleteOverQuery = get(query, 'description', '');
    },

    openFilters() {
      this.$emit('open-filters');
    },
    queryGeolocation() {
      if (this.geoipLocation) {
        this.textSearchQuery = this.nearLocation(this.geoipLocation);
        this.search();
        this.isGeolocationQueryScheduled = false;
      } else {
        this.autoDetectLocation();
      }
    },
    search() {
      this.$emit('input', this.textSearchQuery);
      this.$emit('search');
    },

    selectSuggestion(suggestion) {
      this.textSearchQuery = suggestion.description;
      this.hideAutoLocation();
      this.search();
    },

    showAutoLocation() {
      this.isGoogleSuggestionsVisible = true;
    },

    hideAutoLocation() {
      delay(() => {
        this.isGoogleSuggestionsVisible = false;
      }, 300);
    },

    scheduleGeolocationQuery() {
      this.isGeolocationQueryScheduled = true;
    },
  },
};
</script>
<style lang="scss" scoped>
@-webkit-keyframes processing {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}
@keyframes processing {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(359deg);
    transform: rotate(359deg);
  }
}

.locator-search {
  .auto-location {
    .processing {
      -webkit-animation: processing 2s infinite linear;
      animation: processing 2s infinite linear;
    }
  }
  .google-suggestions {
    position: absolute;
    top: 100%;
    width: 100%;
    padding-right: 3.8em;
    z-index: 3;

    li {
      $border-style: 1px solid var(--gray-20);

      border-left: $border-style;
      border-right: $border-style;
      border-bottom: $border-style;
      background: var(--white);
      padding: 0.5rem 0.5rem 0.5rem 1rem;
      cursor: pointer;

      &:first-child {
        border-top: $border-style;
      }

      &:hover {
        color: var(--white);
        background: var(--blue-100);
        border-color: var(--blue-100);
      }
    }
  }
}
</style>
